import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import SendIcon from '@material-ui/icons/Send';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import allActions from '../../../../../../actions';
import { TrademateAPI } from '../../../../../../API/trademate';
import { unoTheme } from '../../../../../../assets/styles/unoTheme';
import { roles, checkUserIsRole } from '../../../../../utils/user';
import UnoButton from '../../../../../utils/UnoButton';
import {
    emptyInstitution, hasStep1Changes, isFilledInstitution, maskInstitutionCnpj, resourceManagerMessage,
    STEP1_REQUIRED_MESSAGES,
} from './institutionUtils';
import { isDocumentFulfilled } from './documentUtils';
import TrademateStep1Data from './TrademateStep1Data';
import TrademateStep2Documents from './TrademateStep2Documents';
import TrademateStep3Confirmation from './TrademateStep3Confirmation';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        //O container pai (.defaultScrollWithHeaderContent) é display:flex, então sem width
        //explícito esta aba encolheria até o tamanho do conteúdo
        width: '100%',
        minWidth: 0,
    },
    content: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'auto',
        padding: '8px 24px 24px 24px',
    },
    contentEmpty: {
        display: "flex",
        width: "100%",
        height: "100%",
        alignItems: 'center',
        justifyContent: 'center',
        overflowY: 'auto',
        overflowX: 'auto',
        padding: '8px 24px 24px 24px',
    },
    footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 24px',
        borderTop: 'solid 1px #e0e0e0',
    },
    footerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    signedCounter: {
        fontSize: '12px',
        color: unoTheme.secondaryColor,
        whiteSpace: 'nowrap',
    },
});

//Exportado porque o stepper é renderizado no header do AccreditationScreen, que precisa dos mesmos
//rótulos. O total também serve de referência para o último passo aqui dentro.
export const TRADEMATE_STEPS = ['Dados', 'Documentos', 'Confirmação'];

/**
 * Custodiantes e liquidantes começam com uma linha em branco já na tela, para o usuário não
 * precisar clicar em "+" antes de digitar o primeiro. Em modo leitura não faz sentido: uma ficha
 * enviada sempre tem pelo menos um de cada, e um input vazio ali seria só ruído.
 */
function withDefaultRow(list, registration) {

    const rows = list.map(i => ({
        name: i.name,
        //O backend grava só os 14 dígitos e o MyTextField só mascara no onChange, então sem isto
        //o CNPJ salvo apareceria cru no input até o usuário digitar nele
        cnpj: maskInstitutionCnpj(i.cnpj),
        //Vem null do banco para liquidante (e para custodiante salvo antes da migration 98);
        //o input precisa de string, senão o MyTextField vira descontrolado
        account_number: i.account_number ? i.account_number : '',
        is_main: i.is_main,
    }));

    if (rows.length > 0) return rows;
    if (registration && registration.status === 'SENT') return rows;

    return [emptyInstitution(true)];
}

/**
 * Descarta as linhas totalmente em branco antes de enviar ao backend.
 *
 * A linha default existe só para a digitação: sem isso, salvar um rascunho recém-aberto seria
 * recusado com "Informe o nome do custodiante 1". Se o principal cair fora nessa limpeza, o
 * primeiro sobrevivente assume — mesma regra da remoção manual na etapa 1.
 */
function cleanInstitutions(list) {

    const rows = list.filter(isFilledInstitution);

    if (rows.length > 0 && !rows.some(item => item.is_main)) {
        return rows.map((item, index) => Object.assign({}, item, { is_main: index === 0 }));
    }

    return rows;
}

/**
 * Etapa em que o wizard abre, derivada do estado da ficha.
 *
 * `SENT` é o único critério de "já passou dos Documentos": desde que o envio passou para o rodapé
 * da Etapa 2, a Etapa 3 é o recibo do que foi submetido — abri-la com a ficha ainda em edição
 * mostraria comprovante de algo que não aconteceu. Por isso `REOPENED` cai em Documentos, junto
 * com `DRAFT`: a ficha existe e o motivo de ter sido reaberta é editá-la.
 *
 * A R4 (cadastro do RPPS incompleto) não entra na conta de propósito: a Etapa 2 tem um painel que
 * explica exatamente o que falta, o que é mais útil que devolver o usuário aos Dados, onde não há
 * nada a corrigir.
 *
 * @param {{registration: object, custodians: object[], settlements: object[], managers: object[]}} data
 * @returns {0|1|2}
 */
export function initialStepFor(data) {

    const registration = data && data.registration ? data.registration : null;

    if (!registration) return 0;
    if (registration.status === 'SENT') return 2;

    //A ficha existir já implica etapa 1 completa (o saveData é all-or-nothing), mas fichas criadas
    //antes dessa regra podem estar pela metade — nesse caso volta para os Dados, em vez de abrir
    //uma Etapa 2 que não teria como gerar nada.
    const step1Complete = data.custodians.length > 0
        && data.settlements.length > 0
        && data.managers.length > 0;

    return step1Complete ? 1 : 0;
}

/**
 * Cadastro Trademate: wizard de 3 etapas sobre a ficha 1:1 do RPPS.
 *
 * Este componente é o único ponto de conversa com a API: as etapas recebem dados e callbacks por
 * props. O estado da etapa 1 vive aqui porque a etapa 3 precisa dele para o resumo.
 *
 * Enquanto a ficha não existe (nenhum save ainda), as etapas 2 e 3 ficam bloqueadas — é a mesma
 * ideia do `onlyInUpdate` das abas de DetailClientScreen.
 */
export default function TrademateTab(props) {

    //O passo é do AccreditationScreen porque o stepper é renderizado no header, ao lado das abas.
    //Toda a lógica de navegação continua aqui; só o dono do valor é que subiu.
    //
    //`onRegistrationChange` leva a ficha para o header, que renderiza o selo de status ao lado do
    //stepper. Tem que ser uma função estável (o setter de useState do pai): uma arrow nova a cada
    //render entraria na dependência do efeito de sincronia e viraria laço infinito.
    const { activeStep, setActiveStep, onRegistrationChange } = props;

    const classes = useStyles();
    const dispatch = useDispatch();

    //checkUserIsRole espera o wrapper { user }, não o user direto
    const currentUser = useSelector(state => state.currentUser);
    const clientOn = useSelector(state => state.clientOn);

    const clientId = clientOn && clientOn.client ? clientOn.client.id : null;

    //Guarda de qual cliente já teve a etapa inicial calculada (ver o efeito de inicialização)
    const initializedForClientRef = useRef(null);
    const [data, setData] = useState(null);
    const [availableManagers, setAvailableManagers] = useState([]);
    const [sendPendencies, setSendPendencies] = useState([]);

    //Estado editável da etapa 1
    const [custodians, setCustodians] = useState([]);
    const [settlements, setSettlements] = useState([]);
    const [managerIds, setManagerIds] = useState([]);
    const [submitted, setSubmitted] = useState(false);

    const registration = data && data.registration ? data.registration : null;
    const status = registration ? registration.status : null;

    const isSent = status === 'SENT';

    const canEditRole = checkUserIsRole(currentUser,
        [roles.manager, roles.advisor, roles.admin, roles.superadmin]);

    const canEdit = canEditRole && !isSent;

    const canReopen = checkUserIsRole(currentUser,
        [roles.advisor, roles.admin, roles.superadmin]) && isSent;

    //Mesma regra do reabrir: o e-mail é da LEMA para a LEMA, e só há o que reenviar se foi enviado
    const canResendEmail = canReopen;

    const applyData = useCallback((body) => {

        setData(body);
        setCustodians(withDefaultRow(body.custodians, body.registration));
        setSettlements(withDefaultRow(body.settlements, body.registration));
        setManagerIds(body.managers.map(m => m.user_id));

    }, []);

    const loadAll = useCallback(async () => {

        if (!clientId) return;

        dispatch(allActions.innerLoadActions.setInnerLoad({ show: true, loadMessage: 'Carregando...' }));

        const [responseData, responseManagers, responsePendencies] = await Promise.all([
            TrademateAPI.getByClientId(clientId),
            TrademateAPI.getAvailableManagers(clientId),
            //Pendências do envio: a Etapa 2 usa para avisar do que não é falta de documento
            TrademateAPI.getPendencies(clientId),
        ]);

        if (responseData.success) {
            applyData(responseData.body);
        } else {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true, type: 'error',
                message: responseData.body && responseData.body.message
                    ? responseData.body.message
                    : 'Erro ao carregar o cadastro Trademate'
            }));
        }

        if (responseManagers.success) {
            setAvailableManagers(responseManagers.body.rows);
        }

        setSendPendencies(responsePendencies.success ? responsePendencies.body.pendencies : []);

        dispatch(allActions.innerLoadActions.setInnerLoad({ show: false }));

    }, [clientId, dispatch, applyData]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    //Trocar de RPPS reinicia o wizard: o passo em que o usuário estava não vale para outra ficha.
    //Zera enquanto o novo cliente carrega; o efeito abaixo corrige quando o `data` chega.
    useEffect(() => {
        setActiveStep(0);
        setSubmitted(false);
        //Limpa o selo do header: durante o carregamento do novo cliente o `data` ainda é o antigo,
        //e um chip "Enviado" de outro RPPS engana mais que um passo desatualizado
        onRegistrationChange(null);
        //Ambos vêm por prop; são setters de useState do pai, portanto estáveis
    }, [clientId, setActiveStep, onRegistrationChange]);

    /**
     * Espelha a ficha no header, que renderiza o selo de status.
     *
     * Efeito separado de propósito: fazer isso dentro do `applyData` mudaria a identidade dele, que
     * é dependência do `loadAll`, que é dependência do efeito de carregamento — um laço que refaria
     * requisição a cada render.
     */
    useEffect(() => {
        onRegistrationChange(data ? data.registration : null);
    }, [data, onRegistrationChange]);

    /**
     * Abre o wizard na etapa correspondente ao estado da ficha — uma vez por cliente.
     *
     * A guarda por ref é o ponto delicado: `applyData` roda a cada refresh (gerar documento,
     * anexar assinado, remover anexo, falha de envio). Sem ela, quem clicasse "Voltar" para os
     * Dados seria empurrado de volta para Documentos no reload seguinte.
     *
     * Trocar de RPPS muda o `clientId`, a guarda falha sozinha e a etapa é recalculada.
     */
    useEffect(() => {

        if (!clientId || !data) return;
        if (initializedForClientRef.current === clientId) return;

        initializedForClientRef.current = clientId;
        setActiveStep(initialStepFor(data));

    }, [clientId, data, setActiveStep]);

    const hasAttachedSigned = useMemo(() => {
        if (!data) return false;
        return data.documents.some(d => d.signed_key);
    }, [data]);

    //R4: pendências do cadastro do RPPS (endereço, Gestor de Recursos). Vêm do backend porque
    //dependem de dados que esta tela não edita.
    const rppsPendencies = useMemo(() => {
        return data && data.rpps_pendencies ? data.rpps_pendencies : [];
    }, [data]);

    const documents = useMemo(() => (data ? data.documents : []), [data]);

    //Conta os gerados assinados e os complementares com ao menos um arquivo
    const signedCount = useMemo(() => documents.filter(isDocumentFulfilled).length, [documents]);

    const allSigned = documents.length > 0 && signedCount === documents.length;

    /**
     * Salva a etapa 1 e avança. O backend zera documentos gerados/assinados quando os dados mudam,
     * então avisamos antes se já houver assinado anexado.
     */
    const doSaveStep1 = async () => {

        setSubmitted(true);

        //As linhas em branco não contam: são exatamente o que o backend descarta
        const cleanedCustodians = cleanInstitutions(custodians);
        const cleanedSettlements = cleanInstitutions(settlements);

        const missing = [];
        if (cleanedCustodians.length === 0) missing.push(STEP1_REQUIRED_MESSAGES.custodians);
        if (cleanedSettlements.length === 0) missing.push(STEP1_REQUIRED_MESSAGES.settlements);

        if (managerIds.length === 0) {
            missing.push(STEP1_REQUIRED_MESSAGES.managers);
        } else {
            //Só faz sentido cobrar o Gestor de Recursos depois de haver algum gestor selecionado
            const resourceMessage = resourceManagerMessage(availableManagers, managerIds);
            if (resourceMessage) missing.push(resourceMessage);
        }

        //Barra antes de qualquer efeito colateral: nada é gravado e a etapa 1 não é deixada.
        //Em particular, antes do confirm de invalidar documentos — não faz sentido perguntar
        //isso para depois recusar a ação de qualquer jeito.
        if (missing.length > 0) {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true, type: 'error', message: missing.join(' • ')
            }));
            return;
        }

        //Só pergunta quando os dados de fato mudaram: salvar sem alterar nada não invalida mais
        //documento nenhum, então o aviso seria só atrito. O backend é quem decide o que cai, e
        //decide por documento — aqui a checagem é por ficha inteira.
        const changed = hasStep1Changes(data, cleanedCustodians, cleanedSettlements, managerIds);

        if (hasAttachedSigned && changed) {
            const confirmed = window.confirm(
                'Alterar os dados invalida os documentos que dependem deles, incluindo os assinados '
                + 'já anexados. Eles precisarão ser gerados e anexados novamente. Deseja continuar?');
            if (!confirmed) return;
        }

        dispatch(allActions.innerLoadActions.setInnerLoad({ show: true, loadMessage: 'Salvando...' }));

        const response = await TrademateAPI.saveData({
            client_id: clientId,
            custodians: cleanedCustodians,
            settlements: cleanedSettlements,
            manager_user_ids: managerIds,
        });

        dispatch(allActions.innerLoadActions.setInnerLoad({ show: false }));

        if (response.success) {

            applyData(response.body);
            setSubmitted(false);
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true, type: 'success', message: 'Dados salvos'
            }));
            setActiveStep(1);

        } else {

            const body = response.body;
            const message = body && body.errors && body.errors.length
                ? body.errors.join(' • ')
                : (body && body.message ? body.message : 'Erro ao salvar os dados');

            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true, type: 'error', message
            }));
        }
    };

    /**
     * Envia o cadastro. Vive aqui, e não na Etapa 3, porque o botão passou para o rodapé da
     * Etapa 2 — a Etapa 3 virou o recibo do envio.
     */
    const doSend = async () => {

        const confirmed = window.confirm(
            'Após o envio o cadastro fica bloqueado para edição e só a LEMA poderá reabri-lo. '
            + 'Deseja enviar para a LEMA?');
        if (!confirmed) return;

        dispatch(allActions.innerLoadActions.setInnerLoad({ show: true, loadMessage: 'Enviando...' }));
        const response = await TrademateAPI.send(clientId);
        dispatch(allActions.innerLoadActions.setInnerLoad({ show: false }));

        if (response.success) {

            applyData(response.body);
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true, type: 'success', message: 'Cadastro Trademate enviado'
            }));
            //O recibo abre já com o estado SENT que veio da própria resposta
            setActiveStep(2);

        } else {

            const body = response.body;
            //O servidor devolve a lista de pendências em `errors`; sem isso o usuário só veria
            //"Erro ao enviar" e não saberia o que corrigir
            const message = body && body.errors && body.errors.length
                ? body.errors.join(' • ')
                : (body && body.message ? body.message : 'Erro ao enviar o cadastro');

            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true, type: 'error', message
            }));
            //Recarrega para a lista de pendências da Etapa 2 refletir o motivo da recusa
            loadAll();
        }
    };

    /**
     * Redispara o e-mail da LEMA. Não muda o cadastro, então não recarrega nada — só confirma
     * para quem clicou quais endereços receberam.
     */
    const doResendEmail = async () => {

        const confirmed = window.confirm(
            'Reenviar o e-mail do Cadastro Trademate para a LEMA, com a planilha e os '
            + 'documentos anexados?');
        if (!confirmed) return;

        dispatch(allActions.innerLoadActions.setInnerLoad({ show: true, loadMessage: 'Reenviando...' }));
        const response = await TrademateAPI.resendEmail(clientId);
        dispatch(allActions.innerLoadActions.setInnerLoad({ show: false }));

        const body = response.body;

        if (response.success) {

            const recipients = body && body.recipients ? body.recipients : [];

            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true, type: 'success', message: recipients.length > 0
                    ? 'E-mail reenviado para ' + recipients.join(', ')
                    : 'E-mail reenviado'
            }));

        } else {

            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true, type: 'error', message: body && body.message
                    ? body.message : 'Erro ao reenviar o e-mail'
            }));
        }
    };

    const handleNext = () => {
        if (activeStep === 0) {
            if (canEdit) {
                doSaveStep1();
            } else {
                setActiveStep(1);
            }
            return;
        }
        setActiveStep(activeStep + 1);
    };

    const handleBack = () => setActiveStep(activeStep - 1);

    if (!clientId) {
        return (
            <div className={classes.root}>
                <div className={classes.contentEmpty}>
                    <Typography>Selecione um RPPS para acessar o Cadastro Trademate.</Typography>
                </div>
            </div>
        );
    }

    //As etapas 2 e 3 só existem depois que a ficha nasceu
    const stepsUnlocked = !!registration;

    return (
        <div className={classes.root}>

            {/* O stepper e o selo de status vivem no header do AccreditationScreen, ao lado das
                abas — o selo chega lá pelo onRegistrationChange */}

            <div className={classes.content}>

                {activeStep === 0 && (
                    <TrademateStep1Data
                        canEdit={canEdit}
                        submitted={submitted}
                        custodians={custodians}
                        setCustodians={setCustodians}
                        settlements={settlements}
                        setSettlements={setSettlements}
                        managerIds={managerIds}
                        setManagerIds={setManagerIds}
                        availableManagers={availableManagers}
                    />
                )}

                {activeStep === 1 && (
                    <TrademateStep2Documents
                        clientId={clientId}
                        canEdit={canEdit}
                        documents={documents}
                        rppsPendencies={rppsPendencies}
                        sendPendencies={sendPendencies}
                        onChanged={loadAll}
                    />
                )}

                {activeStep === 2 && (
                    <TrademateStep3Confirmation
                        clientId={clientId}
                        canReopen={canReopen}
                        data={data}
                        onChanged={loadAll}
                    />
                )}

            </div>

            <div className={classes.footer}>

                <div>
                    {activeStep > 0 && (
                        <UnoButton
                            size='small'
                            type='default'
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}>
                            {'Voltar'}
                        </UnoButton>
                    )}
                </div>

                <div className={classes.footerRight}>

                    {/* Passo 0: segue navegando (salvando, quando editável) */}
                    {activeStep === 0 && (
                        <UnoButton
                            size='small'
                            type='primary'
                            startIcon={<ArrowForwardIcon />}
                            disabled={!stepsUnlocked && !canEdit}
                            onClick={handleNext}>
                            {canEdit ? 'Salvar e avançar' : 'Avançar'}
                        </UnoButton>
                    )}

                    {/* Passo 1 (Documentos): é daqui que o cadastro é enviado. Três casos —
                        ficha já enviada só navega para o recibo; bloqueada pela R4 não oferece
                        envio; editável mostra o contador e o botão de envio. */}
                    {activeStep === 1 && isSent && (
                        <UnoButton
                            size='small'
                            type='primary'
                            startIcon={<ArrowForwardIcon />}
                            onClick={handleNext}>
                            {'Avançar'}
                        </UnoButton>
                    )}

                    {activeStep === 1 && !isSent && rppsPendencies.length === 0 && (
                        <>
                            <span className={classes.signedCounter}>
                                <strong style={{ color: "black" }}>{signedCount + ' de ' + documents.length}</strong> {' '}
                                {documents.length === 1 ? 'documento anexado' : 'documentos anexados'}
                            </span>
                            {canEdit && (
                                <UnoButton
                                    size='small'
                                    type='primary'
                                    startIcon={<SendIcon />}
                                    disabled={!allSigned}
                                    onClick={doSend}>
                                    {'Enviar para a LEMA'}
                                </UnoButton>
                            )}
                        </>
                    )}

                    {/* Passo 2 (recibo): não há mais nada a enviar, só a opção de a LEMA
                        redisparar o e-mail que carrega a planilha e os assinados */}
                    {activeStep === 2 && canResendEmail && (
                        <UnoButton
                            size='small'
                            type='primary'
                            startIcon={<SendIcon />}
                            onClick={doResendEmail}>
                            {'Reenviar e-mail'}
                        </UnoButton>
                    )}

                </div>

            </div>

        </div>
    );
}
